import { useMutation } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/constants';
import { useQueryWithLogging } from '@/hooks/useQueryWithLogging';
import Service, {
  Constraint,
  TransmitterRequest,
  VariableInfo,
} from '@/lib/App';

export function useFetchVariables({
  name = '',
  domains = null,
  domainRequired = false,
}: {
  name?: string;
  domains?: string[] | null;
  domainRequired?: boolean;
}) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.variables(name, domains),
    queryFn: () => Service.planner.fetchVariables({ name, domains }),
    retry: 3,
    enabled: (domainRequired && domains !== null) || !domainRequired,
  });
}

export function useGetVariable(
  variableName: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.variable(variableName),
    queryFn: () => Service.planner.getVariable(variableName),
    ...options,
  });
}

export function useVariableStats(
  variableName: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.variableStats(variableName),
    queryFn: () => Service.planner.fetchStats(variableName),
    ...options,
  });
}

export function useUpdateVariable(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: async (variable: VariableInfo) =>
      await Service.planner.updateVariable(variable),
    ...options,
  });
}

export function useVariableValue(
  variableName: string,
  backend: string,
  constraints: any[],
  triggerFetch: boolean = false,
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.variableValue(variableName),
    queryFn: () => {
      const request = [
        constraints
          .filter(({ target, value }) => !!target && !!value)
          .reduce(
            (map, { target, value }) => {
              map[target] = value;
              return map;
            },
            { var: [variableName] },
          ),
      ];

      return Service.planner.getTransmitterResponse(backend, request);
    },
    enabled: triggerFetch,
  });
}

export function useArchiveVariable(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: async (variableName: string) =>
      await Service.planner.archiveVariable(variableName),
    ...options,
  });
}

export function useGetVar(
  variableName: string,
  variableService: string,
  constraints: Constraint[],
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.getVar(variableName),
    queryFn: () => {
      const request: TransmitterRequest = [
        constraints
          .filter(
            ({ target, value }: Constraint): boolean => !!target && !!value,
          )
          .reduce(
            (
              map: TransmitterRequest,
              { target, value }: Constraint,
            ): TransmitterRequest => {
              map[target] = value;
              return map;
            },
            { var: [variableName] },
          ),
      ];

      return Service.planner!.getTransmitterResponse(variableService, request);
    },
    ...options,
  });
}

export function useCreateVariable(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: async (variable: VariableInfo) =>
      await Service.planner.createVariable(variable),
    ...options,
  });
}

export function useDeleteVariable(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: (variableName: string) => {
      return Service.planner.deleteVariable(variableName);
    },
    ...options,
  });
}

export function useGetRollout(
  variableName: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.rollout(variableName),
    queryFn: () => Service.planner.fetchRollout(variableName),
    enabled: !!variableName,
    ...options,
  });
}
